/**
 * NSDC-approved micro-credential catalogue (display only — nothing here is scored).
 *
 * Static catalogue: /data/certifications.json (built by scripts/build-certifications.py
 * from the NSDC micro-credential list). Official provider links and summaries are
 * looked up on the web and stored in `certification_links`.
 */
import { supabase } from "@/integrations/supabase/client";
import { loadCareersBundle } from "@/data/careersBundle";

export interface Certification {
  id: string;
  name: string;
  provider: string;
  programs: string[];
  tracks: string[];
  degrees: string[];
  tools: string[];
  prereqs: string[];
  level: string | null;
  nsqf: string | null;
  duration: string | null;
  careers: string[]; // ci_ids
}

export interface CertLink {
  cert_id: string;
  url: string | null;
  title: string | null;
  summary: string | null;
  source_domain: string | null;
}

let catalogPromise: Promise<Certification[]> | null = null;
export function loadCertifications(): Promise<Certification[]> {
  if (!catalogPromise) {
    catalogPromise = fetch("/data/certifications.json", { cache: "force-cache" })
      .then((r) => r.json())
      .then((d) => d.certifications as Certification[])
      .catch((e) => { catalogPromise = null; throw e; });
  }
  return catalogPromise;
}

export async function loadCertLinks(ids?: string[]): Promise<Record<string, CertLink>> {
  let q = supabase.from("certification_links").select("cert_id,url,title,summary,source_domain");
  if (ids?.length) q = q.in("cert_id", ids);
  const { data } = await q;
  return Object.fromEntries((data ?? []).map((r) => [r.cert_id, r as CertLink]));
}

/** Certifications mapped to a career, foundation level first. */
export async function certsForCareer(ciId: string, limit = 6): Promise<Certification[]> {
  const all = await loadCertifications();
  return all.filter((c) => c.careers.includes(ciId)).sort(byLevel).slice(0, limit);
}

/** Certifications for a set of careers (e.g. a learner's matches), ranked by overlap. */
export async function certsForCareers(ids: string[], limit = 6): Promise<Array<Certification & { hits: number }>> {
  const all = await loadCertifications();
  const set = new Set(ids);
  return all
    .map((c) => ({ ...c, hits: c.careers.filter((x) => set.has(x)).length }))
    .filter((c) => c.hits > 0)
    .sort((a, b) => b.hits - a.hits || byLevel(a, b))
    .slice(0, limit);
}

/** Resolve learner match ids/names to ci_ids. */
export async function resolveCareerIds(matches: Array<{ id: string; name: string }>): Promise<string[]> {
  const bundle = await loadCareersBundle();
  const byName = new Map(bundle.rows.map((r) => [r.name.toLowerCase(), r.ciId]));
  const ids = new Set(bundle.rows.map((r) => r.ciId));
  return matches
    .map((m) => (ids.has(m.id) ? m.id : byName.get(m.name.toLowerCase())))
    .filter((x): x is string => Boolean(x));
}

const LEVEL_ORDER: Record<string, number> = { Foundation: 0, Associate: 1, Expert: 2 };
function byLevel(a: Certification, b: Certification) {
  return (LEVEL_ORDER[a.level ?? ""] ?? 1) - (LEVEL_ORDER[b.level ?? ""] ?? 1);
}

export type CertStatus = "saved" | "completed";

export async function loadMyCertStatus(userId: string): Promise<Record<string, CertStatus>> {
  const { data } = await supabase.from("user_certifications").select("cert_id,status").eq("user_id", userId);
  return Object.fromEntries((data ?? []).map((r) => [r.cert_id, r.status as CertStatus]));
}

export async function setMyCertStatus(userId: string, certId: string, status: CertStatus | null) {
  if (!status) {
    return supabase.from("user_certifications").delete().eq("user_id", userId).eq("cert_id", certId);
  }
  return supabase
    .from("user_certifications")
    .upsert({ user_id: userId, cert_id: certId, status, updated_at: new Date().toISOString() });
}
